import type { OrderOptionDto } from '../dto/order';
import {
  getOrderProductDetailDisplayName,
  getOrderProductDisplayDimensions,
  getOrderProductVariantLabels,
  type OrderProductVariantLabel,
} from '../modules/orders/orderProductDisplay';
import { sanitizeProductDisplayName } from '../utils/productDisplayNameSanitizer';
import {
  asQuantityUnit,
  convertCommercialQuantity,
  createQuantitySet,
  getQuantitySetValue,
  hasPersistedQuantitySet,
  isAllocationUnit,
  quantitySetToFields,
  resolveStoredQuantitySet,
  scaleQuantitySetToAmount,
} from '../utils/commercialQuantity';
import type { AllocationUnit } from '../utils/dimensionConversion';
import type { QuantityUnit } from '../utils/commercialQuantity';
import type {
  AllocationHintMap,
  SharedTransportCargoItem,
} from './sharedTransportFormUtils';

// Row/form-value shapes shared between SharedTransportForm and its extracted
// section components. Kept out of the section components so both the
// orchestrator (which derives the rows) and the presentational sections (which
// render them) import one canonical definition.
export interface CargoTableRow extends SharedTransportCargoItem {
  key: string;
  orderNumber: string;
  availableQuantity: number;
  allocatedElsewhere: number;
  productDimensions?: string | null;
  productVariantLabels?: OrderProductVariantLabel[];
  /** Catalog variant of the order line: pairs it with a ZD line (form only, not sent). */
  productVariantId?: string | null;
  /** 1-based position of the order line („poz. N”), when the order data carries it (form only, not sent). */
  orderLineNumber?: number;
}

export interface CargoItemFormValue {
  id?: string;
  quantity?: number | null;
  unit?: QuantityUnit | null;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  goodsValueTotal?: number | null;
  manualAllocatedTransportCost?: number | null;
}

export function buildCargoKey(orderId: string, productId: string): string {
  return `${orderId}:${productId}`;
}

type CargoQuantitySnapshotSource = Pick<
  CargoItemFormValue,
  'quantityPcs' | 'quantityM2' | 'quantityM3'
>;

/**
 * A persisted quantity set is one atomic snapshot. Never fill an explicit NULL
 * component from the current order line: doing so would silently rewrite the
 * historical conversion after product dimensions or ratios change.
 */
export function buildCargoQuantitySnapshotFields(
  product: CargoQuantitySnapshotSource,
  existingCargoItem?: CargoQuantitySnapshotSource | null,
): Required<CargoQuantitySnapshotSource> {
  const source = existingCargoItem
    && hasPersistedQuantitySet(existingCargoItem as Record<string, unknown>)
    ? existingCargoItem
    : product;

  return {
    quantityPcs: source.quantityPcs ?? null,
    quantityM2: source.quantityM2 ?? null,
    quantityM3: source.quantityM3 ?? null,
  };
}

/**
 * Transport order lines can still contain legacy names assembled from a
 * business name, a product code, technical variant id and dimensions. Keep
 * those values searchable in their source records, but never repeat them in
 * the operator-facing picker or cargo card. Prefer the canonical catalog name
 * when the API provides it and use the same sanitizer as product selectors.
 */
export function getTransportOrderProductDisplayName(
  product: OrderOptionDto['products'][number],
): string {
  const technicalValues = [
    product.id,
    product.productVariantId,
    product.productCode,
    product.productEan,
  ];

  for (const candidate of [product.catalogProductName, product.name]) {
    const displayName = sanitizeProductDisplayName(candidate, technicalValues);
    if (displayName) return displayName;
  }

  return 'Produkt';
}

export function buildCargoRows(
  selectedOrders: OrderOptionDto[],
  cargoItemsByKey: Record<string, CargoItemFormValue>,
  allocationHints: AllocationHintMap,
  existingCargoItems: SharedTransportCargoItem[] = [],
  basicProductLabel = 'Produkt podstawowy',
): CargoTableRow[] {
  return selectedOrders.flatMap((order) =>
    order.products.map((product) => {
      const key = buildCargoKey(order.id, product.id);
      const hint = allocationHints[product.id];
      const formItem = cargoItemsByKey[key] || {};
      const existingCargoItem = existingCargoItems.find((item) =>
        item.orderId === order.id && item.orderProductId === product.id
      );
      const dimensionSource = {
        ...product,
        width: existingCargoItem?.width ?? product.width,
        height: existingCargoItem?.height ?? product.height,
        length: existingCargoItem?.length ?? product.length,
        thickness: existingCargoItem?.thickness ?? product.thickness,
      };
      dimensionSource.width ??= product.catalogProductWidth;
      dimensionSource.height ??= product.catalogProductHeight;
      dimensionSource.length ??= product.catalogProductLength;
      dimensionSource.thickness ??= product.catalogProductThickness;
      const physicalQuantity = toNumberValue(
        formItem.quantity,
        existingCargoItem?.quantity ?? product.quantity ?? 0,
      );
      const physicalUnit = asQuantityUnit(
        formItem.unit ?? existingCargoItem?.unit ?? product.unit ?? 'pcs',
      );
      const commercialUnit = (existingCargoItem?.commercialUnit || product.saleUnit) as AllocationUnit | undefined;
      const snapshotSource = hasPersistedQuantitySet(formItem as unknown as Record<string, unknown>)
        ? formItem
        : hasPersistedQuantitySet(existingCargoItem as unknown as Record<string, unknown>)
          ? existingCargoItem
          : product;
      const storedQuantitySet = resolveStoredQuantitySet(
        snapshotSource as unknown as Record<string, unknown>,
      );
      const hasStoredComponent = isAllocationUnit(physicalUnit)
        && getQuantitySetValue(storedQuantitySet, physicalUnit) !== null;
      const quantitySet = isAllocationUnit(physicalUnit)
        ? hasStoredComponent
          ? scaleQuantitySetToAmount(storedQuantitySet, physicalQuantity, physicalUnit)
            ?? { pcs: null, m2: null, m3: null }
          : createQuantitySet(physicalQuantity, physicalUnit, dimensionSource)
        : { pcs: null, m2: null, m3: null };
      const convertedCommercial = commercialUnit && commercialUnit !== physicalUnit
        ? getQuantitySetValue(quantitySet, commercialUnit)
          ?? (!hasPersistedQuantitySet(snapshotSource as unknown as Record<string, unknown>)
            && isAllocationUnit(physicalUnit)
            ? convertCommercialQuantity(physicalQuantity, physicalUnit, commercialUnit, dimensionSource).value
            : null)
        : null;
      const productQuantitySet = resolveStoredQuantitySet(product as unknown as Record<string, unknown>);
      const availableQuantity = isAllocationUnit(physicalUnit)
        ? getQuantitySetValue(productQuantitySet, physicalUnit)
          ?? (asQuantityUnit(product.unit ?? 'pcs') === physicalUnit ? Number(product.quantity || 0) : 0)
        : asQuantityUnit(product.unit ?? 'pcs') === physicalUnit ? Number(product.quantity || 0) : 0;
      const allocatedRatio = hint && Number(hint.orderedQuantity) > 0
        ? Math.max(Number(hint.allocatedQuantity) || 0, 0) / Number(hint.orderedQuantity)
        : 0;
      const allocatedElsewhere = availableQuantity * allocatedRatio;

      return {
        key,
        id: formItem.id || existingCargoItem?.id,
        orderId: order.id,
        orderProductId: product.id,
        productVariantId: product.productVariantId ?? null,
        ...(Number(product.lineNumber) > 0 ? { orderLineNumber: Number(product.lineNumber) } : {}),
        orderNumber: order.displayOrderNumber || order.orderNumber,
        productName: getOrderProductDetailDisplayName(
          product,
          getTransportOrderProductDisplayName(product),
        ),
        productDimensions: getOrderProductDisplayDimensions(dimensionSource),
        productVariantLabels: getOrderProductVariantLabels(product, basicProductLabel),
        quantity: physicalQuantity,
        ...quantitySetToFields(quantitySet),
        deliveredQuantity: existingCargoItem?.deliveredQuantity ?? null,
        deliveredQuantityPcs: existingCargoItem?.deliveredQuantityPcs ?? null,
        deliveredQuantityM2: existingCargoItem?.deliveredQuantityM2 ?? null,
        deliveredQuantityM3: existingCargoItem?.deliveredQuantityM3 ?? null,
        deliveryConfirmedAt: existingCargoItem?.deliveryConfirmedAt ?? null,
        deliveryConfirmedById: existingCargoItem?.deliveryConfirmedById ?? null,
        commercialQuantity: convertedCommercial,
        commercialUnit: convertedCommercial == null ? null : commercialUnit,
        availableQuantity,
        allocatedElsewhere,
        unit: physicalUnit,
        goodsValueTotal: toNumberValue(formItem.goodsValueTotal, existingCargoItem?.goodsValueTotal ?? 0),
        manualAllocatedTransportCost: toNullableNumberValue(
          formItem.manualAllocatedTransportCost,
          existingCargoItem?.manualAllocatedTransportCost ?? null,
        ),
        grossWeight: product.grossWeight,
        // Display fallback for „Waga całkowita” (the backend snapshot falls back the same way).
        catalogGrossWeight: product.catalogProductGrossWeight,
        // A persisted cargo snapshot wins in edit mode, followed by the
        // historical order-line dimensions and finally the linked catalog
        // product. Missing catalog fallbacks here made both the visible
        // dimensions and m²/m³ metrics disappear for modern catalog lines.
        width: dimensionSource.width ?? product.catalogProductWidth,
        height: dimensionSource.height ?? product.catalogProductHeight,
        length: dimensionSource.length ?? product.catalogProductLength,
        thickness: dimensionSource.thickness ?? product.catalogProductThickness,
        // „Liczymy tylko w sztukach” (migration 196): no m² / m³ on the truck.
        countInPiecesOnly: (existingCargoItem?.countInPiecesOnly ?? product.countInPiecesOnly) === true,
      };
    })
  );
}

// Stable dependency-array key for effects that recompute cargo-derived totals
// (weight/volume/dimensions summary) from a fresh CargoTableRow[] snapshot.
// MUST include every metrics input the summary can change on — a key missing
// one lets a memoized effect skip re-running and keep stale derived values.
export function buildCargoRowsMetricsKey(rows: CargoTableRow[]): string {
  return JSON.stringify(rows.map((row) => ({
    key: row.key,
    quantity: row.quantity,
    unit: row.unit,
    quantityPcs: row.quantityPcs,
    quantityM2: row.quantityM2,
    quantityM3: row.quantityM3,
    width: row.width,
    length: row.length,
    height: row.height,
    thickness: row.thickness,
  })));
}

// JULIET (MED "stale Pozostało hint"): how much of an order product's
// quantity remains available to load onto ANOTHER transport, once both
// what's already loaded elsewhere AND what the operator is CURRENTLY typing
// into this transport's own "Do załadunku" field are accounted for. Extracted
// as a pure function so the "Pozostało" cell hint (TransportOrdersCargoSection)
// and the "overflow" guard on the loaded-quantity cell can share one formula
// and never disagree about what "remaining" means. Never negative — a
// mid-typing quantity that already exceeds availability shows 0 remaining,
// not a misleading negative number. The overflow warning deliberately does
// not block save: the backend's audited operational-exception flow asks the
// operator to confirm and justify the unusual allocation.
export function deriveRemainingElsewhereQuantity(
  availableQuantity: number,
  allocatedElsewhere: number,
  currentQuantity: number,
): number {
  return Math.max(availableQuantity - allocatedElsewhere - (Number(currentQuantity) || 0), 0);
}

export function toNumberValue(value: unknown, fallback: number): number {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

export function toNullableNumberValue(value: unknown, fallback: number | null): number | null {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

// Like toNullableNumberValue, but with NO fallback: empty/undefined/invalid
// always resolves to null. For fields where the FORM FIELD ITSELF is already
// the single source of truth — falling back to a snapshot value would
// silently resurrect a value the operator just explicitly cleared.
export function toFiniteOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}
